import React from 'react';
import { PhoneCountryCodeType } from '@bus/models';
import { Button } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import { createOptionsList } from '../../../services/common/miscUtils';
import { getMaxDateOfIncorporationDate } from '../../../services/common/validation';
import { CompanyClassificationEnum, CorrespondenceMethodEnum, NoYesEnum } from '../../../types/forms';
import { DatePickerFormField } from '../../Fieldset/DatePickerFormField';
import { NumericInputFormField } from '../../Fieldset/NumericInputFormField';
import { PhoneNumberFormField } from '../../Fieldset/PhoneNumberFormField';
import { SelectFormField } from '../../Fieldset/SelectFormField';
import { TextInputFormField } from '../../Fieldset/TextInputFormField';
import { DropdownOption } from '../../UsedCarSelectionForm/UsedCarSelectionFormModels';
import { FormField } from '../..';
import { nonBreakingSpace } from '../../../utils/constants';
import { buildFieldName } from '../../../utils/fieldNameUtils';

export const createFormInputs = (formTranslationKey: string, disabled = false, fieldNamePrefix?: string) => {
  return {
    tradingName: () => (
      <TextInputFormField
        fieldName="tradingName"
        labelTranslationKey={`${formTranslationKey}:fields:tradingName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:tradingName:placeholder`}
        isDisabled={disabled}
      />
    ),
    legalName: () => (
      <TextInputFormField
        fieldName="legalName"
        labelTranslationKey={`${formTranslationKey}:fields:legalName:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:legalName:placeholder`}
        isDisabled={disabled}
      />
    ),
    vatLiable: () => (
      <SelectFormField
        fieldName="vatLiable"
        labelTranslationKey={`${formTranslationKey}:fields:vatLiable:label`}
        optionList={createOptionsList(NoYesEnum, 'companyDetailsForm', 'noYes', null)}
        isDisabled={disabled}
      />
    ),
    vatNumber: () => (
      <TextInputFormField
        fieldName="vatNumber"
        labelTranslationKey={`${formTranslationKey}:fields:vatNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:vatNumber:placeholder`}
        isDisabled={disabled}
      />
    ),
    croNumber: () => (
      <TextInputFormField
        fieldName="croNumber"
        labelTranslationKey={`${formTranslationKey}:fields:croNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:croNumber.placeholder`}
        isDisabled={disabled}
      />
    ),
    croNumberButton: (isDisabled: boolean) => (
      <FormField
        name="croNumberButton"
        labelText={nonBreakingSpace} // An empty string or simple space would cause a broken layout
        isDisabled={isDisabled || disabled}
        render={() => (
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          <Button name="croNumberButton" type="button" onClick={() => {}} disabled={isDisabled || disabled} full>
            {t(`${formTranslationKey}:fields:croNumber:label`)}
          </Button>
        )}
      />
    ),
    dateOfIncorporation: () => (
      <DatePickerFormField
        fieldName="dateOfIncorporation"
        labelTranslationKey={`${formTranslationKey}:fields:dateOfIncorporation:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:dateOfIncorporation:placeholder`}
        isDisabled={disabled}
        maxDate={getMaxDateOfIncorporationDate()}
      />
    ),
    businessType: (options: Array<DropdownOption>) => (
      <SelectFormField
        fieldName="businessType"
        labelTranslationKey={`${formTranslationKey}:fields:businessType:label`}
        isDisabled={disabled}
        optionList={options}
      />
    ),
    businessSubType: (options: Array<DropdownOption>) => (
      <SelectFormField
        fieldName="businessSubType"
        labelTranslationKey={`${formTranslationKey}:fields:businessSubType:label`}
        isDisabled={disabled}
        optionList={options}
      />
    ),
    countryCode: () => (
      <SelectFormField
        fieldName={buildFieldName('countryCode', fieldNamePrefix)}
        labelTranslationKey={`${formTranslationKey}:fields:countryCode:label`}
        optionList={createOptionsList(PhoneCountryCodeType, formTranslationKey, 'countryCode')}
        isDisabled={disabled}
      />
    ),
    phoneNumber: () => (
      <PhoneNumberFormField
        fieldName={buildFieldName('phoneNumber', fieldNamePrefix)}
        labelTranslationKey={`${formTranslationKey}:fields:phoneNumber:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:phoneNumber:placeholder`}
        isDisabled={disabled}
        type="mobile"
      />
    ),
    email: () => (
      <TextInputFormField
        fieldName={buildFieldName('email', fieldNamePrefix)}
        labelTranslationKey={`${formTranslationKey}:fields:email:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:email:placeholder`}
        isDisabled={disabled}
      />
    ),
    fleetSize: () => (
      <NumericInputFormField
        fieldName="fleetSize"
        labelTranslationKey={`${formTranslationKey}:fields:fleetSize:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:fleetSize.placeholder`}
        isDisabled={disabled}
        type="numeric"
        maximumFractionDigits={0}
      />
    ),
    companyClassification: () => (
      <SelectFormField
        fieldName="companyClassification"
        labelTranslationKey={`${formTranslationKey}:fields:companyClassification:label`}
        optionList={createOptionsList(CompanyClassificationEnum, formTranslationKey, 'companyClassification')}
        isDisabled={disabled}
      />
    ),
    correspondenceMethod: () => (
      <SelectFormField
        fieldName="correspondenceMethod"
        labelTranslationKey={`${formTranslationKey}:fields:correspondenceMethod:label`}
        optionList={createOptionsList(CorrespondenceMethodEnum, formTranslationKey, 'correspondenceMethod')}
        isDisabled={disabled}
      />
    ),
    turnover: () => (
      <NumericInputFormField
        fieldName="turnover"
        labelTranslationKey={`${formTranslationKey}:fields:turnover:label`}
        placeholderTranslationKey={`${formTranslationKey}:fields:turnover.placeholder`}
        isDisabled={disabled}
        type="money"
      />
    ),
  };
};
